/**
 * PipelineStageList — the vertical five-stage timeline (DS processing →
 * validation → ChannelMeter upload → lock payments → payout sheets). Owns the
 * per-stage gating (mirrors the backend preconditions exactly) and hands each
 * row's presentation to PipelineStageRow. The GAM report (stage 0) renders as
 * its own card above this list.
 */

import React from 'react';
import { Button, Card } from '../../ui';
import {
  PipelineRun,
  GamReportUploadMeta,
  PayoutTarget,
  PipelineRerunStep,
} from '../../../services/adminApi';
import PipelineStageRow, { Disclosure } from './PipelineStageRow';
import PipelineStage1Reruns from './PipelineStage1Reruns';
import PipelinePayoutTargets from './PipelinePayoutTargets';
import ValidationPreview from '../ValidationPreview';
import PipelinePayoutResults from '../PipelinePayoutResults';
import { stagePill, isStageUnblocked, StagePill } from './pipelineUtils';
import type { ConfirmStep } from './ConfirmStageModal';

export type PipelineTabKey = 'processing' | 'financials';

interface PipelineStageListProps {
  pipeline: PipelineRun | null;
  uploadMeta: GamReportUploadMeta | null;
  monthDisplay: string;
  actionBusy: string | null;
  /** Pre-start dry-run draft (header/mode state lives in PipelineTab) */
  draftDryRun: boolean;
  onDraftDryRunChange: (v: boolean) => void;
  onStart: () => void;
  onRestart: () => void;
  onCancelProcessing: () => void;
  onRunValidation: () => void;
  onConfirmStep: (step: ConfirmStep) => void;
  onGeneratePayouts: (targets?: PayoutTarget[]) => Promise<void>;
  onRerunStage1: (step: PipelineRerunStep) => Promise<{ runId: string; message: string }>;
  onNavigateTab: (tab: PipelineTabKey) => void;
}

const fmtWhen = (iso?: string): string => (iso ? new Date(iso).toLocaleString() : '');

const PipelineStageList: React.FC<PipelineStageListProps> = ({
  pipeline,
  uploadMeta,
  monthDisplay,
  actionBusy,
  draftDryRun,
  onDraftDryRunChange,
  onStart,
  onRestart,
  onCancelProcessing,
  onRunValidation,
  onConfirmStep,
  onGeneratePayouts,
  onRerunStage1,
  onNavigateTab,
}) => {
  const auto = pipeline?.autoAdvance === true;
  const busy = actionBusy !== null;

  const ds = pipeline?.stages.dsProcessing;
  const validation = pipeline?.stages.validation;
  const cmUpload = pipeline?.stages.cmUpload;
  const cmLock = pipeline?.stages.cmLock;
  const payouts = pipeline?.stages.payoutSheets;

  // ---- Row 1: DS processing -------------------------------------------------
  const dsPill: StagePill = !pipeline
    ? uploadMeta
      ? { variant: 'pending', label: 'ready' }
      : { variant: 'draft', label: 'waiting' }
    : stagePill(pipeline, 'dsProcessing', ds);

  const dsAction = (() => {
    if (!pipeline) {
      return (
        <>
          <label style={styles.dryRunLabel}>
            <input
              type="checkbox"
              checked={draftDryRun}
              onChange={(e) => onDraftDryRunChange(e.target.checked)}
              disabled={busy}
            />
            Dry run
          </label>
          <Button
            variant="primary"
            size="sm"
            onClick={onStart}
            disabled={!uploadMeta || busy}
            title={uploadMeta ? undefined : `Upload the ${monthDisplay} GAM report first`}
          >
            {actionBusy === 'start' ? 'Starting…' : 'Start processing'}
          </Button>
        </>
      );
    }
    if (ds?.status === 'running') {
      return (
        <>
          <Button variant="ghost" size="sm" onClick={() => onNavigateTab('processing')}>
            View run log →
          </Button>
          <Button variant="ghost" size="sm" onClick={onCancelProcessing} disabled={busy}>
            {actionBusy === 'cancel' ? 'Cancelling…' : 'Cancel run'}
          </Button>
        </>
      );
    }
    if (ds?.status === 'failed') {
      return (
        <>
          <Button variant="ghost" size="sm" onClick={() => onNavigateTab('processing')}>
            View run log →
          </Button>
          <Button variant="primary" size="sm" onClick={onRestart} disabled={busy}>
            {actionBusy === 'start' ? 'Starting…' : 'Retry processing'}
          </Button>
        </>
      );
    }
    return null;
  })();

  const dsMeta = ds?.status === 'completed'
    ? `Completed ${fmtWhen(ds.completedAt)}${ds.runId ? ` · run ${ds.runId}` : ''}`
    : ds?.status === 'running' && ds.startedAt
      ? `Started ${fmtWhen(ds.startedAt)}`
      : undefined;

  // ---- Row 2: Validation ----------------------------------------------------
  const validationPill = stagePill(pipeline, 'validation', validation);
  const validationUnblocked = isStageUnblocked(pipeline, 'validation');
  const validationHasResult = validation?.status === 'completed' || validation?.status === 'failed';
  const validationIssues =
    (validation?.result?.summary?.blockers ?? 0) + (validation?.result?.summary?.warnings ?? 0);

  const validationAction = validationUnblocked && validation?.status !== 'running' ? (
    <Button variant="ghost" size="sm" onClick={onRunValidation} disabled={busy}>
      {actionBusy === 'validation' ? 'Running…' : validationHasResult ? 'Re-run validation' : 'Run validation'}
    </Button>
  ) : null;

  const validationNudge = pipeline?.dryRun && !validationHasResult
    ? 'Dry runs skip the Drive writes, so validation cannot see a completed run — expect a blocker until a real run exists.'
    : auto && !validationUnblocked && validation?.status === 'pending'
      ? 'Runs automatically after processing completes.'
      : undefined;

  // ---- Row 3: ChannelMeter upload -------------------------------------------
  const cmUploadPill = stagePill(pipeline, 'cmUpload', cmUpload);
  const cmUploadUnblocked = isStageUnblocked(pipeline, 'cmUpload');
  const cmUploadAction = cmUploadUnblocked && cmUpload?.status === 'pending' ? (
    <Button variant="primary" size="sm" onClick={() => onConfirmStep('cmUpload')} disabled={busy}>
      Confirm CM upload
    </Button>
  ) : null;
  const cmUploadMeta = cmUpload?.status === 'confirmed'
    ? `Confirmed by ${cmUpload.confirmedBy || '—'} · ${fmtWhen(cmUpload.confirmedAt)}${cmUpload.notes ? ` · ${cmUpload.notes}` : ''}`
    : undefined;
  const cmUploadNudge =
    cmUploadUnblocked && cmUpload?.status === 'pending' && validation?.status !== 'completed'
      ? 'Validation has not passed yet — you can still confirm, but review it first.'
      : undefined;

  // ---- Row 4: Lock payments ---------------------------------------------------
  const cmLockPill = stagePill(pipeline, 'cmLock', cmLock);
  const cmLockUnblocked = isStageUnblocked(pipeline, 'cmLock');
  const cmLockAction = cmLockUnblocked && cmLock?.status === 'pending' ? (
    <Button variant="spark" size="sm" onClick={() => onConfirmStep('cmLock')} disabled={busy}>
      Confirm payment lock
    </Button>
  ) : null;
  const cmLockMeta = cmLock?.status === 'confirmed'
    ? `Confirmed by ${cmLock.confirmedBy || '—'} · ${fmtWhen(cmLock.confirmedAt)}`
    : undefined;

  // ---- Row 5: Payout sheets ---------------------------------------------------
  const payoutsPill = stagePill(pipeline, 'payoutSheets', payouts);
  const payoutsUnblocked = isStageUnblocked(pipeline, 'payoutSheets');
  const payoutsEnabled = payoutsUnblocked && payouts?.status !== 'running';

  const payoutsAction = payoutsUnblocked && payouts?.status === 'pending' ? (
    <Button variant="primary" size="sm" onClick={() => onGeneratePayouts()} disabled={busy}>
      {actionBusy === 'payouts' ? 'Starting…' : 'Generate payout sheets'}
    </Button>
  ) : payouts?.status === 'failed' && payoutsUnblocked ? (
    <Button variant="primary" size="sm" onClick={() => onGeneratePayouts()} disabled={busy}>
      {actionBusy === 'payouts' ? 'Starting…' : 'Retry generation'}
    </Button>
  ) : null;

  const payoutsMeta = payouts?.status === 'running'
    ? `Generating: ${(payouts.activeTargets?.length ? payouts.activeTargets : ['all sheets']).join(', ')}`
    : payouts?.status === 'completed'
      ? `Completed ${fmtWhen(payouts.completedAt)}`
      : undefined;

  // Sheets generated BEFORE the latest processing run (a restart keeps the
  // payout result) — point the operator at the per-sheet re-runs.
  const payoutsStale = Boolean(
    payouts?.status === 'completed' &&
    payouts.completedAt &&
    ds?.status === 'completed' &&
    ds.completedAt &&
    payouts.completedAt < ds.completedAt
  );

  const payoutsNudge = auto && payouts?.status === 'pending' && !payoutsUnblocked
    ? 'Generates automatically once payments are locked.'
    : payoutsStale
      ? 'Generated before the latest processing run — re-run the sheets that depend on the new data below.'
      : undefined;

  return (
    <Card style={styles.card}>
      <PipelineStageRow
        index={1}
        title="DS processing"
        description="Parses the GAM report, applies rate cards, and writes the master + channel tracker sheets."
        stage={ds}
        pill={dsPill}
        meta={dsMeta}
        action={dsAction}
        error={ds?.status === 'failed' ? ds.error : undefined}
      >
        {/* The scoped re-runs read the month's STORED upload — they only need an
            upload to exist, not a completed run. Keep them reachable after a
            failed/cancelled run so the operator isn't forced into a full
            "Retry processing" (which resets every downstream stage). */}
        {pipeline && uploadMeta && (ds?.status === 'completed' || ds?.status === 'failed') && (
          <Disclosure summary="Output re-runs" defaultOpen={ds.status === 'failed'}>
            <PipelineStage1Reruns
              pipeline={pipeline}
              actionBusy={actionBusy}
              onRerun={onRerunStage1}
              onRestart={onRestart}
              onViewRunLog={() => onNavigateTab('processing')}
            />
          </Disclosure>
        )}
      </PipelineStageRow>

      <PipelineStageRow
        index={2}
        title="Validation"
        description="Checks channel coverage, revenue anomalies, rate-card completeness, and mapping staleness."
        stage={validation}
        pill={validationPill}
        action={validationAction}
        nudge={validationNudge}
        error={validation?.status === 'failed' && validation.error ? validation.error : undefined}
      >
        {pipeline && validationHasResult && (
          <Disclosure
            summary={validation?.status === 'failed' ? 'Review blockers' : 'View checks'}
            defaultOpen={validation?.status === 'failed' || validationIssues > 0}
          >
            <ValidationPreview
              month={pipeline.month}
              showApprove={cmUpload?.status === 'pending'}
              onApprove={() => onConfirmStep('cmUpload')}
            />
            {validation?.status === 'failed' && (
              <div style={styles.blockerHint}>
                Fix the issues above, re-run processing if needed, then re-run validation.
              </div>
            )}
          </Disclosure>
        )}
      </PipelineStageRow>

      <PipelineStageRow
        index={3}
        title="ChannelMeter upload"
        description="Upload the Direct Sold and AdSense payout reports to ChannelMeter, then confirm here."
        stage={cmUpload}
        pill={cmUploadPill}
        meta={cmUploadMeta}
        action={cmUploadAction}
        nudge={cmUploadNudge}
      />

      <PipelineStageRow
        index={4}
        title="Lock payments"
        description="Lock payments in ChannelMeter, then confirm. This can't be undone."
        stage={cmLock}
        pill={cmLockPill}
        meta={cmLockMeta}
        action={cmLockAction}
      />

      <PipelineStageRow
        index={5}
        title="Payout sheets"
        description="Writes the AdSense + Direct Sold consolidated sheets, tracker summaries, and the agency tabs."
        stage={payouts}
        pill={payoutsPill}
        meta={payoutsMeta}
        action={payoutsAction}
        nudge={payoutsNudge}
        error={payouts?.status === 'failed' ? payouts.error : undefined}
        isLast
      >
        {payouts?.status === 'completed' && <PipelinePayoutResults payout={payouts} />}
        {/* Per-sheet targets are valid whenever the backend gate (cmLock confirmed)
            is open — including a pending stage after a pipeline reset, so one
            sheet can be regenerated without a full "Generate payout sheets". */}
        {pipeline && (payouts?.status === 'completed' || payouts?.status === 'failed' || (payoutsUnblocked && payouts?.status === 'pending')) && (
          <Disclosure
            summary={payouts?.status === 'pending' ? 'Per-sheet runs' : 'Per-sheet re-runs'}
            defaultOpen={payouts?.status === 'failed'}
          >
            <PipelinePayoutTargets
              pipeline={pipeline}
              enabled={payoutsEnabled}
              actionBusy={actionBusy}
              onGenerate={onGeneratePayouts}
            />
          </Disclosure>
        )}
      </PipelineStageRow>
    </Card>
  );
};

const styles: Record<string, React.CSSProperties> = {
  card: {
    padding: '24px 24px 8px',
  },
  dryRunLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12.5,
    color: 'var(--fg-2)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  blockerHint: {
    marginTop: 8,
    fontSize: 12.5,
    color: 'var(--fg-3)',
  },
};

export default PipelineStageList;
